import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateWorkspaceAccount } from '@/mocks';

const WS_SESSION_KEY = 'yh_workspace_session';
const WS_AUTO_KEY = 'yh_workspace_auto_expiry';
const WS_USER_KEY = 'yh_workspace_user';

export function isWorkspaceLoggedIn(): boolean {
  const expiry = localStorage.getItem(WS_AUTO_KEY);
  if (expiry && Date.now() < parseInt(expiry, 10)) return true;
  return sessionStorage.getItem(WS_SESSION_KEY) === '1';
}

export function getWorkspaceUser(): string | null {
  return sessionStorage.getItem(WS_USER_KEY) || localStorage.getItem(WS_USER_KEY);
}

export function workspaceLogout(): void {
  sessionStorage.removeItem(WS_SESSION_KEY);
  sessionStorage.removeItem(WS_USER_KEY);
  localStorage.removeItem(WS_AUTO_KEY);
  localStorage.removeItem(WS_USER_KEY);
}

const STORAGE_KEY = 'yh_remembered_workspace';

export default function AdminWorkspaceLoginPage() {
  const navigate = useNavigate();
  const [account, setAccount] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberAccount, setRememberAccount] = useState(false);
  const [autoLogin, setAutoLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isWorkspaceLoggedIn()) {
      navigate('/admin-workspace', { replace: true });
      return;
    }
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      setAccount(saved);
      setRememberAccount(true);
    }
  }, [navigate]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!account.trim()) { setError('请输入账号'); return; }
    if (!password.trim()) { setError('请输入密码'); return; }

    const user = validateWorkspaceAccount(account, password);
    if (!user) {
      setError('账号或密码错误，请联系超管确认');
      return;
    }

    if (rememberAccount) localStorage.setItem(STORAGE_KEY, account.trim());
    else localStorage.removeItem(STORAGE_KEY);

    if (autoLogin) {
      localStorage.setItem(WS_AUTO_KEY, String(Date.now() + 7 * 24 * 60 * 60 * 1000));
      localStorage.setItem(WS_USER_KEY, account.trim());
    } else {
      localStorage.removeItem(WS_AUTO_KEY);
    }

    sessionStorage.setItem(WS_SESSION_KEY, '1');
    sessionStorage.setItem(WS_USER_KEY, account.trim());

    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate('/admin-workspace', { replace: true });
    }, 1200);
  };

  const primary = '#7c3aed';
  const primaryDark = '#5b21b6';
  const primaryLight = '#ede9fe';
  const primaryMid = '#c4b5fd';
  const headerBg = 'linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4c1d95 100%)';

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'linear-gradient(160deg, #f5f3ff 0%, #faf9ff 50%, #ede9fe 100%)' }}>
      {/* 顶部装饰带 */}
      <div
        className="relative overflow-hidden flex-shrink-0 h-[160px] md:h-[220px]"
        style={{ background: headerBg }}
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }} />
        <div className="absolute -bottom-24 -left-12 w-56 h-56 rounded-full" style={{ background: 'rgba(255,255,255,0.05)' }} />
        <div className="absolute top-1/2 right-8 w-3 h-3 rounded-full" style={{ background: 'rgba(255,255,255,0.3)' }} />
        <div className="absolute top-8 left-16 w-2 h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.25)' }} />
        <div className="absolute bottom-10 right-20 w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />

        {/* 品牌信息 */}
        <div className="relative z-10 flex flex-col items-center justify-end h-full pb-8">
          <div
            className="w-16 h-16 rounded-2xl bg-white flex items-center justify-center mb-3 overflow-hidden"
            style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}
          >
            <img
              src="/static/logo-placeholder.svg"
              alt="语焰"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-xl font-bold text-white tracking-wide">官方工作台</h1>
          <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.65)' }}>语焰·数字人才市场&nbsp;</p>
        </div>
      </div>

      {/* 登录卡片 */}
      <div className="flex-1 flex flex-col px-5 -mt-5 relative z-10">
        <div className="bg-white rounded-3xl w-full max-w-sm mx-auto" style={{ boxShadow: '0 4px 40px rgba(124,58,237,0.12)' }}>
          <div className="px-6 pt-7 pb-2">
            <h2 className="text-lg font-bold text-slate-900 mb-0.5">工作台登录</h2>
            <p className="text-xs text-slate-400 mb-6">请使用授权账号登录官方工作台</p>

            <form onSubmit={handleLogin} className="space-y-4">
              {/* 账号 */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">账号</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-shield-user-line text-base" style={{ color: primary }} />
                  </div>
                  <input
                    type="text"
                    value={account}
                    onChange={e => setAccount(e.target.value)}
                    placeholder="请输入账号"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border text-sm text-slate-800 placeholder-slate-300 focus:outline-none transition-all bg-slate-50"
                    style={{ borderColor: '#e2e8f0' }}
                    onFocus={e => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryLight}`; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>

              {/* 密码 */}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1.5">密码</label>
                <div className="relative">
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-lock-2-line text-base" style={{ color: primary }} />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="请输入密码"
                    className="w-full pl-10 pr-11 py-3 rounded-xl border text-sm text-slate-800 placeholder-slate-300 focus:outline-none transition-all bg-slate-50"
                    style={{ borderColor: '#e2e8f0' }}
                    onFocus={e => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 3px ${primaryLight}`; }}
                    onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer transition-colors"
                  >
                    <i className={`ri-${showPassword ? 'eye-off' : 'eye'}-line text-base`} />
                  </button>
                </div>
              </div>

              {/* 记住账号 */}
              <div className="flex items-center justify-between">
                <label
                  className="flex items-center gap-2 cursor-pointer select-none"
                  onClick={() => setRememberAccount(!rememberAccount)}
                >
                  <div
                    className="w-4 h-4 rounded flex items-center justify-center transition-all flex-shrink-0"
                    style={{
                      background: rememberAccount ? primary : 'transparent',
                      border: rememberAccount ? `1px solid ${primary}` : '1px solid #cbd5e1',
                    }}
                  >
                    {rememberAccount && <i className="ri-check-line text-white" style={{ fontSize: 10 }} />}
                  </div>
                  <span className="text-xs text-slate-500">记住账号</span>
                </label>
              </div>

              {/* 自动登录 7 天 */}
              <div
                className="flex items-center justify-between p-3 rounded-xl cursor-pointer select-none"
                style={{
                  background: autoLogin ? '#f5f3ff' : '#f8fafc',
                  border: `1px solid ${autoLogin ? primaryMid : '#e2e8f0'}`,
                }}
                onClick={() => setAutoLogin(!autoLogin)}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: autoLogin ? primary : '#e2e8f0' }}
                  >
                    <i className={`ri-timer-flash-line text-sm ${autoLogin ? 'text-white' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-slate-700">自动登录 7 天</div>
                    <div className="text-xs text-slate-400">7天内免密码直接进入工作台</div>
                  </div>
                </div>
                <div
                  className="w-10 h-5 rounded-full transition-all flex-shrink-0 relative"
                  style={{ background: autoLogin ? primary : '#e2e8f0' }}
                >
                  <div
                    className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                    style={{ left: autoLogin ? 'calc(100% - 18px)' : 2 }}
                  />
                </div>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="flex items-center gap-2 rounded-xl px-3.5 py-2.5" style={{ background: '#fff1f2', border: '1px solid #fecdd3' }}>
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0" />
                  <span className="text-xs text-red-600">{error}</span>
                </div>
              )}

              {/* 登录按钮 */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white transition-all cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                style={{
                  background: loading ? primaryMid : `linear-gradient(135deg, ${primaryDark} 0%, ${primary} 100%)`,
                  boxShadow: loading ? 'none' : '0 4px 16px rgba(124,58,237,0.35)',
                }}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin text-base" /><span>验证中...</span></>
                ) : (
                  <><i className="ri-login-circle-line text-base" /><span>进入工作台</span></>
                )}
              </button>
            </form>
          </div>

          {/* 安全标识 */}
          <div className="px-6 py-4 flex items-center justify-center gap-5" style={{ borderTop: '1px solid #f1f5f9' }}>
            {[
              { icon: 'ri-shield-check-line', label: '权限隔离' },
              { icon: 'ri-lock-line', label: '加密传输' },
              { icon: 'ri-eye-off-line', label: '操作审计' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-1 text-xs text-slate-400">
                <i className={`${item.icon}`} style={{ color: primary, opacity: 0.7 }} />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-5 mb-8">
          © 2026 语焰·数字人才市场 官方工作台
        </p>
      </div>
    </div>
  );
}
